import { deepmerge } from '@mui/utils';
import { createTheme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import { DEFAULTCOLOR_TEXT_DISABLED } from '../default/palette';
import { generateDefaultThemeOptions } from '../default';
import type { Device } from '../default';
import generateFontFaceDeclarations from '../../utils/generateFontFaceDeclarations';
import PantonRegular from './fonts/PantonNarrow-Regular.woff';
import PantonBold from './fonts/PantonNarrow-Bold.woff';
import PantonBlack from './fonts/PantonNarrow-Black.woff';

const DEFAULTCOLOR_TEXT_DARK = '#424242';
const DEFAULTCOLOR_TEXT_LIGHT = '#ffffff';
const DEFAULTCOLOR_TEXT_TRANSPARENT_DARK = 'rgba(66, 66, 66, 0.6)';
const DEFAULTCOLOR_TEXT_TRANSPARENT_LIGHT = 'rgba(255, 255, 255, 0.6)';

const FONT_FAMILY = 'Panton, Arial, Helvetica, sans-serif';
export const FONTS = {
  Panton: {
    400: PantonRegular,
    700: PantonBold,
    800: PantonBlack,
  },
};
const FONTFACE_DECLARATIONS = generateFontFaceDeclarations(FONTS);

export const FONT_PRELOADS = [FONTS.Panton[400], FONTS.Panton[700], FONTS.Panton[800]];

/** Define theme object.
 * See here for more info: https://mui.com/customization/theming/ */
const themeOptions: ThemeOptions = {
  // https://mui.com/customization/palette/
  palette: {
    primary: {
      main: '#e11937',
      textTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_LIGHT,
    },
    secondary: {
      main: '#2d4691',
    },
    grey: {
      main: '#B4B4B4',
      light: '#eae8e5',
      dark: '#424242',
    },
    text: {
      disabled: DEFAULTCOLOR_TEXT_DISABLED,
      primary: DEFAULTCOLOR_TEXT_DARK,
      secondary: DEFAULTCOLOR_TEXT_TRANSPARENT_DARK,
      dark: DEFAULTCOLOR_TEXT_DARK,
      darkTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_DARK,
      light: DEFAULTCOLOR_TEXT_LIGHT,
      lightTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_LIGHT,
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    fontWeightRegular: 400,
    fontWeightBold: 700,
    fontWeightBlack: 800,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: FONTFACE_DECLARATIONS,
    },
    MuiButton: {
      styleOverrides: {
        root: ({
          theme: {
            unstable_sx: sx,
            palette: { grey },
          },
        }) =>
          sx({
            ':disabled': { bgcolor: grey.main, color: grey.dark },
          }),
      },
    },
  },
};

/** Merge with default theme options and create a theme instance
 * See here for more info: https://mui.com/customization/theming/#api */
const theme = (device: Device) =>
  createTheme(deepmerge(generateDefaultThemeOptions(device), themeOptions));

// eslint-disable-next-line import/no-default-export
export default theme;
